import React from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    title: '1. Information We Collect',
    body: 'When you create or use an account, we may collect your name, mobile number, profile information, and login-related information. Messages, images, files, community posts, and call-related information may be processed as necessary to provide and operate our services.'
  },
  {
    title: '2. How We Use Your Information',
    body: 'Your information is used to manage your account, verify your identity, provide chat and calling services, send notifications, maintain security, and improve your experience. We will not sell or rent your personal information for advertising without your consent.'
  },
  {
    title: '3. Messages, Calls, and Shared Content',
    body: 'You control the content you share on Nexora. However, people you share it with may copy, save, or forward it to others. Reports of abuse, fraud, harassment, or illegal activity may be handled in accordance with applicable law and our community guidelines.'
  },
  {
    title: '4. Device Permissions and Third-Party Services',
    body: 'Nexora may request device permissions for features such as calls, notifications, camera access, microphone access, and file sharing. If you do not grant a permission, the related feature may not work. Secure infrastructure providers such as Firebase may be used to manage accounts, store data, and deliver notifications.'
  },
  {
    title: '5. Data Security and Retention',
    body: 'We use reasonable technical and administrative measures to protect your information. However, no method of transmission or storage over the internet can be guaranteed to be completely secure. Information may be retained while your account is active or as needed for legal and service-related purposes.'
  },
  {
    title: '6. Your Rights',
    body: 'You may review or update your profile information. To close your account, contact us using the details below. You may also request that we limit the use of your information, except where processing is required for legal, security, or service purposes.'
  },
  {
    title: '7. Children’s Privacy',
    body: 'Nexora is not intended for children. If a minor provides personal information without parental consent, please contact us so that appropriate action can be taken.'
  },
  {
    title: '8. Changes to This Policy',
    body: 'This Privacy & Policy may be updated when our services, legal requirements, or security practices change. By continuing to use Nexora after an update, you acknowledge the revised policy.'
  }
];

const PrivacyPolicy = () => (
  <main className="privacy-page">
    <style>{`
      .privacy-page {
        min-height: 100vh;
        padding: 28px 20px 48px;
        background: var(--privacy-bg, #f8fafc);
        color: var(--privacy-text, #0f172a);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      }
      :root[data-theme="dark"] .privacy-page {
        --privacy-bg: #000000;
        --privacy-surface: #181818;
        --privacy-text: #f8fafc;
        --privacy-muted: #a1a1aa;
        --privacy-border: #2a2a2a;
      }
      .privacy-shell { width: 100%; max-width: 780px; margin: 0 auto; }
      .privacy-back { display: inline-flex; margin-bottom: 24px; color: #16a34a; font-size: 14px; font-weight: 700; text-decoration: none; }
      .privacy-header { padding: 30px; border-radius: 24px; background: var(--privacy-surface, #ffffff); border: 1px solid var(--privacy-border, #e2e8f0); box-shadow: 0 16px 40px rgba(15, 23, 42, 0.06); }
      .privacy-kicker { margin: 0 0 8px; color: #16a34a; font-size: 12px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
      .privacy-title { margin: 0; font-size: clamp(28px, 6vw, 42px); line-height: 1.1; }
      .privacy-intro { margin: 14px 0 0; color: var(--privacy-muted, #64748b); font-size: 15px; line-height: 1.7; }
      .privacy-updated { margin: 18px 0 0; color: var(--privacy-muted, #64748b); font-size: 12px; }
      .privacy-content { margin-top: 18px; padding: 10px 30px; border-radius: 24px; background: var(--privacy-surface, #ffffff); border: 1px solid var(--privacy-border, #e2e8f0); }
      .privacy-section { padding: 22px 0; border-bottom: 1px solid var(--privacy-border, #e2e8f0); }
      .privacy-section:last-child { border-bottom: 0; }
      .privacy-section h2 { margin: 0 0 8px; font-size: 17px; }
      .privacy-section p { margin: 0; color: var(--privacy-muted, #64748b); font-size: 14px; line-height: 1.8; }
      .privacy-contact { margin: 18px 0 0; padding: 22px 30px; border-left: 4px solid #22c55e; border-radius: 4px 16px 16px 4px; background: var(--privacy-surface, #ffffff); border-top: 1px solid var(--privacy-border, #e2e8f0); border-right: 1px solid var(--privacy-border, #e2e8f0); border-bottom: 1px solid var(--privacy-border, #e2e8f0); }
      .privacy-contact p { margin: 6px 0 0; color: var(--privacy-muted, #64748b); font-size: 14px; line-height: 1.7; }
      @media (max-width: 560px) { .privacy-page { padding: 20px 14px 36px; } .privacy-header { padding: 24px 20px; } .privacy-content { padding: 8px 20px; } .privacy-contact { padding: 20px; } }
    `}</style>

    <div className="privacy-shell">
      <Link to="/login" className="privacy-back">← Back to Login</Link>

      <header className="privacy-header">
        <p className="privacy-kicker">Nexora Trust Center</p>
        <h1 className="privacy-title">Privacy &amp; Policy</h1>
        <p className="privacy-intro">Your privacy matters to us. This policy explains how Nexora collects, uses, and protects your information in a clear and transparent way.</p>
        <p className="privacy-updated">Last updated: September 8, 2026</p>
      </header>

      <section className="privacy-content">
        {sections.map(section => (
          <article className="privacy-section" key={section.title}>
            <h2>{section.title}</h2>
            <p>{section.body}</p>
          </article>
        ))}
      </section>

      <section className="privacy-contact">
        <strong>Contact Us</strong>
        <p>If you have questions about this policy or your information, contact us through Nexora Settings or email us at <a href="mailto:official@nexora.community" style={{ color: '#16a34a', fontWeight: 700 }}>official@nexora.community</a>.</p>
      </section>
    </div>
  </main>
);

export default PrivacyPolicy;